import { WS_BASE_URL } from '../../config/onlineConfig';

const RECONNECT_BASE_DELAY_MS = 500;
const RECONNECT_MAX_DELAY_MS = 5000;
const SPECTATOR_TERMINAL_CLOSE_CODES = new Set([4003, 4004, 4013]);

/**
 * Thin WebSocket wrapper for a live online game (RONDA_ONLINE.md Fase 9b —
 * `/ws?token=&roomId=`, same auth handshake as the REST API). Auto-reconnects
 * with a capped exponential backoff on an unexpected close; dispatches
 * incoming messages by `type` to registered handlers.
 */
export class GameWsClient {
    /**
     * @param {string} roomId
     * @param {() => string | null} getAccessToken - called on every (re)connect, not just once, so a token refreshed meanwhile is picked up.
     */
    constructor(roomId, getAccessToken, { spectate = false } = {}) {
        this.roomId = roomId;
        this.getAccessToken = getAccessToken;
        /** Read-only connection (`&spectate=1`) — the server sends the table without any hand and ignores everything but REQUEST_STATE. */
        this.spectate = spectate;
        /** @type {(() => void) | null} Spectators only: fired instead of reconnecting when the server closes with a code a retry can never fix (game over/not watchable/full). */
        this.onTerminalClose = null;
        this.ws = null;
        this.handlers = new Map();
        this.reconnectAttempts = 0;
        this.reconnectTimer = null;
        this.manuallyClosed = false;
        /** @type {((connected: boolean) => void) | null} */
        this.onConnectionChange = null;
        /** @type {((attempt: number) => void) | null} Fired each time a reconnect is scheduled, with the 1-based attempt number — lets the UI show "retrying (attempt N)" instead of a static message (Fase 9c). */
        this.onReconnecting = null;
    }

    on(type, handler) {
        if (!this.handlers.has(type)) this.handlers.set(type, new Set());
        this.handlers.get(type).add(handler);
        return () => this.handlers.get(type)?.delete(handler);
    }

    connect() {
        this.manuallyClosed = false;
        const token = this.getAccessToken();
        if (!token) return; // no session — nothing to connect with (logged out mid-game, etc.)

        const url = `${WS_BASE_URL}?token=${encodeURIComponent(token)}&roomId=${encodeURIComponent(this.roomId)}${this.spectate ? '&spectate=1' : ''}`;
        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
            this.reconnectAttempts = 0;
            this.onConnectionChange?.(true);
        };

        this.ws.onmessage = (event) => {
            let msg;
            try {
                msg = JSON.parse(event.data);
            } catch {
                return;
            }
            this.handlers.get(msg.type)?.forEach((fn) => fn(msg));
        };

        this.ws.onclose = (event) => {
            this.onConnectionChange?.(false);
            if (this.manuallyClosed) return;
            // 4003: not watchable (private/not in progress) · 4004: no live
            // game for this room (it ended) · 4013: spectator limit reached.
            if (this.spectate && SPECTATOR_TERMINAL_CLOSE_CODES.has(event.code)) {
                this.manuallyClosed = true;
                this.onTerminalClose?.();
                return;
            }
            this.scheduleReconnect();
        };

        // onerror is always followed by onclose for a WebSocket — reconnect is handled there.
        this.ws.onerror = () => {};
    }

    scheduleReconnect() {
        const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** this.reconnectAttempts, RECONNECT_MAX_DELAY_MS);
        this.reconnectAttempts += 1;
        this.onReconnecting?.(this.reconnectAttempts);
        this.reconnectTimer = setTimeout(() => this.connect(), delay);
    }

    send(obj) {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(obj));
        }
    }

    close() {
        this.manuallyClosed = true;
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
        this.ws?.close();
    }
}
